import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule, FormControlName } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import * as _ from 'lodash';
import { AuthService } from '../../@services/auth.service';
import { UsersService } from '../../@services/users.service';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { NgxCsvParserModule, NgxCsvParser } from 'ngx-csv-parser';
import { ToastService } from '../../@services/toast.service';
import { SetFloatingFilter } from '../../@shared/set-floating-filter.component';
import { ScannersService } from '../../@services/scanners.service';
import { BillingService } from '../../@services/billing.service';

const localLog = console.log;
@Component({
  selector: 'app-billing',
  imports: [AgGridAngular, CommonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './billing.component.html',
  styleUrl: './billing.component.scss'
})
export class BillingComponent {

  subs: any = [];
  gridDarkTheme = agGridThemes.dark;
  roles: any[] = [];
  grid: any;
  data: any[] = [];
  filtered_data: any[] = [];
  selected_user: any;
  theme: any;
  scanners: any[] = [];
  login_months: any[] = [];
  create_months: any[] = [];
  deactivate_months: any[] = [];
  customers: any[] = [
    { id: 'alpha-shark', name: 'Alpha Shark'},
    { id: 'delphi-sig', name: 'Delphi Signal'},
    { id: 'mta', name: 'MTA'},
    { id: 'option-pit', name: 'Option Pit'},
    { id: 'simpler-trading', name: 'Simpler Trading'},
    { id: 'strat-alerts', name: 'Strat Alerts'},
    { id: 'theo-trade', name: 'Theo Trade'},
  ];

  filterScanners = new FormControl([]);
  filterCustomers = new FormControl<string[]>([]);
  filterLoginMonths = new FormControl([]);
  filterCreateMonths = new FormControl([]);
  filterDeactivateMonths = new FormControl([]);
  mtaOptionsCheck = false;

  constructor(private authService: AuthService,
    private adminService: UsersService,
    private billingService : BillingService,
    private toastService: ToastService,
    private scannersService: ScannersService
  ) {
    this.init_grid();
    this.theme = this.gridDarkTheme;
  }

  async ngOnInit() {
    this.get_roles();
    this.generate_months();
    await this.get_scanners();
    // Auto-load the initial roster so the panel isn't a No-Rows-To-Show
    // wall on first navigation. Previously required a Refresh click to
    // populate; Mike 2026-07-06 caught the miss. Backend reads from
    // Redis USER_CACHE (feedback_user_display_reads_cache_never_cognito)
    // so this is sub-second even fleet-wide.
    await this.get_billing_users();
  }

  ngOnDestroy(): void {}

  async get_roles() {
    let res = await this.adminService.getRoles();
    if (!res?.err) {
      this.roles = res;
    }
    else {
      this.roles = [];
    }
  }

  async get_billing_users() {
    let query: any = {};
    if (this.filterScanners.value && this.filterScanners.value.length > 0) query.scanners = this.filterScanners.value;
    if (this.filterCustomers.value && this.filterCustomers.value.length > 0) query.customers = this.filterCustomers.value;
    if (this.filterLoginMonths.value && this.filterLoginMonths.value.length > 0) query.login_months = this.filterLoginMonths.value;
    if (this.filterCreateMonths.value && this.filterCreateMonths.value.length > 0) query.create_months = this.filterCreateMonths.value;
    if (this.filterDeactivateMonths.value && this.filterDeactivateMonths.value.length > 0) query.deactivate_months = this.filterDeactivateMonths.value;
    if (this.mtaOptionsCheck && this.filterCustomers.value?.includes('mta')) query.mta_options = true;
    let res: any = await this.billingService.getBillingUsers(query);

    if (res && !res.err) {
      this.data = res;
    }
    else {
      this.data = [];
    }
  }

  async get_scanners() {
    this.scanners = [];
    let res = await this.scannersService.getScanners();
    console.log(res);
    if (res && !res.err) this.scanners = res;
  }

  async refresh() {
    this.selected_user = null;
    await this.get_roles();
    await this.get_billing_users();
  }

  async clear_filters() {
    // Reset form controls
    this.filterScanners.reset([]);
    this.filterCustomers.reset([]);
    this.filterLoginMonths.reset([]);
    this.filterCreateMonths.reset([]);
    this.filterDeactivateMonths.reset([]);

    // Clear grid filters
    if (this.grid.api) {
      this.grid.api.setFilterModel(null);
    }
  }

  generate_months() {
    const login_start = new Date(2025, 7);
    const create_start = new Date(2025, 2);
    const now = new Date();
    const avail_login_months: { id: string; name: string }[] = [];
    const avail_create_months: { id: string; name: string }[] = [];

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Generate available login months (August 2025 start)
    let current_login = new Date(login_start);
    while (current_login <= now) {
      const year = current_login.getFullYear();
      const month = current_login.getMonth();

      const id = `${year}-${String(month + 1).padStart(2, '0')}`;
      const name = `${monthNames[month]} ${year}`;

      avail_login_months.push({ id, name });
      current_login.setMonth(month + 1);
    }

    this.login_months = avail_login_months;

    // Generate available create months (March 2025 start)
    let current_create = new Date(create_start);
    while (current_create <= now) {
      const year = current_create.getFullYear();
      const month = current_create.getMonth();

      const id = `${year}-${String(month + 1).padStart(2, '0')}`;
      const name = `${monthNames[month]} ${year}`;

      avail_create_months.push({ id, name });
      current_create.setMonth(month + 1);
    }

    this.create_months = avail_create_months;
    this.deactivate_months = avail_create_months;
  }


  /**** Users Grid ****/
  init_grid() {
  
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        },
        defaultColDef: {
          floatingFilter: true,
          // filter: 'agTextColumnFilter',
          filter: true,
          cellStyle: { 'text-align': 'left'},
        },
        getRowId: (params: any) => {
          return params.data.username;
        },
        onRowClicked: this.on_row_clicked.bind(this),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs: [
        {
          field: "enabled",
          headerName: 'Enabled',
          width: 95,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else if (params.value === false) {
              return '<i class="fa fa-circle-xmark text-red-800"></i>';
            }
            else {
              return '?';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: [true, false]
              }
            }
          },
        },
        {
          field: "username",
          headerName: 'Username',
          hide: true,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "attributes.name",
          headerName: 'First Name',
          minWidth: 112,
          floatingFilterComponent: SetFloatingFilter,
          editable: true,
        },
        {
          field: "attributes.family_name",
          headerName: 'Last Name',
          minWidth: 112,
          floatingFilterComponent: SetFloatingFilter,
          editable: true,
        },
        {
          field: "attributes.email",
          headerName: 'Email',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "attributes.custom:customers",
          headerName: 'Customers',
          minWidth: 108,
          filter: 'agTextColumnFilter',
          cellRenderer: (params: any) => {
            let val = params.value || '';
            let customers = val.split(',');
            let customer_names = ``;
            _.each(customers, (id: any) => {
              if (!id) return;
              customer_names += `<span class="bg-gray-400 text-xs text-black px-1 rounded-xs">${id}</span>`;
            });

            return `<div class="flex items-center h-full">
              <div class="flex-1 flex gap-1">${customer_names}</div>
            </div>`
          }
        },
        {
          field: "attributes.custom:scanners",
          headerName: 'Scanners',
          minWidth: 108,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (params: any) => {
            let val = params.value || '';
            if (params.node.isSelected()) {
              return `<div class="flex items-center h-full">
                <button class="flex-0 mr-1 px-2 bg-app-teal-100 text-white text-xs rounded-xs cursor-pointer" data-button-action="edit-scanners">edit</button>
                <div class="flex-1 flex gap-1 text-xs">${val}</div>
              </div>`
            }
            else {
              let scanner_ids = val.split(',');
              let scanner_names = ``;
              _.each(scanner_ids, (id: any) => {
                if (!id) return;
                scanner_names += `<span class="bg-gray-400 text-xs text-black px-1 rounded-xs">${id}</span>`;
              });

              return `<div class="flex items-center h-full">
                <div class="flex-1 flex gap-1">${scanner_names}</div>
              </div>`
            }
          },
          editable: true,
        },
        {
          field: "attributes.custom:live_data_access",
          headerName: 'Live Data',
          width: 100,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          valueGetter: (params: any) => {
            let val = params.data.attributes['custom:live_data_access'];
            if (!val || val !== 'true') return false;
            else return true;
          },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else {
              return '';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: ['true', 'false']
              }
            }
          },
        },
        {
          field: "attributes.custom:last_login",
          headerName: 'Last Login',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data.attributes['custom:last_login'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a ZZZZ');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "created_at",
          headerName: 'Created',
          cellStyle: { 'text-align': 'left', color: '#b1b1b1' },
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['created_at'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a ZZZZ');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
      ]
    }
  }
  on_grid_ready(e: any) {
    // console.log('on_grid_ready()');
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    this.grid.api.sizeColumnsToFit();
  }
  on_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.grid?.api) {
      this.filtered_data = [];
      this.grid?.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_data.push(node.data);
      });
    }
  }
  on_row_selected(e: any) {
    // console.log('on_row_selected', e);
    if (e?.node?.isSelected() === true) {
      this.selected_user = e.node.data;
    }

    //Force refresh of scanners column to show edit button
    this.grid.api.refreshCells({
      columns: ['attributes.custom:scanners'],
      force: true
    });
  }
  on_row_clicked(e: any) {
    if (e.event.target !== undefined) {
      let user = e.data;
      let action = e.event.target.getAttribute('data-button-action');
      switch (action) {
        case 'edit-scanners':
          //this.on_edit_user_scanners(user);
          break;
        default:
          break;
      }
    }
  }
  async get_scanner_values() {
    if (!this.scanners || this.scanners.length === 0) {
      await this.get_scanners();
    }

    if (!this.scanners.length) return [];

    return this.scanners.map((s: any) => {
      return s.Id;
    });
  }
}
